import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertOctagon, CheckCircle2, Loader2, ShieldAlert, Wrench } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ProjectFormDialog } from "@/components/ProjectFormDialog";
import { ComplianceFixDialog } from "@/components/ComplianceFixDialog";
import { DuplicateGroups } from "@/components/DuplicateGroups";
import { ExportButton } from "@/components/ExportButton";
import { InconsistencySlicers } from "@/components/InconsistencySlicers";
import { SmartFixButton } from "@/components/SmartFixButton";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

const GROUP_COLOR = {
  missing: "hsl(var(--at-risk))",
  conflict: "hsl(0 72% 42%)",
  mismatch: "hsl(25 90% 48%)",
};
const PAGE = 25;

const Badge = ({ group, count, label, testId }) =>
  count ? (
    <span
      data-testid={testId}
      className="inline-flex items-center gap-1 border px-1.5 py-0.5 font-display text-[10px] font-bold uppercase tracking-wider"
      style={{ borderColor: GROUP_COLOR[group], color: GROUP_COLOR[group] }}
    >
      <AlertOctagon className="h-3 w-3" />
      {label} · {count}
    </span>
  ) : null;

/** One usecase that is not yet clean, with every problem the portal can prove. */
const Row = ({ row, groups, onFix, busy, canSmartFix, onSmartFixed }) => (
  <tr className="align-top" data-testid={`incon-row-${row.code}`}>
    <td className="px-4 py-3">
      <div className="font-data text-[11px] text-muted-foreground">{row.code}</div>
      <div className="max-w-[320px] text-sm font-semibold leading-snug">{row.title}</div>
      <div className="mt-1 font-data text-[10px] uppercase tracking-wider text-muted-foreground">
        {row.plant} · {row.status}
        {row.phase ? ` · ${row.phase}` : ""}
      </div>
      {row.light && (
        <span
          className="mt-1 inline-block border border-[hsl(var(--healthy))] px-1.5 py-0.5 font-display text-[9px] font-bold uppercase tracking-wider text-[hsl(var(--healthy))]"
          data-testid={`incon-light-${row.code}`}
        >
          Completion date only
        </span>
      )}
    </td>
    <td className="px-4 py-3">
      <div className="flex flex-wrap gap-1.5">
        {groups.map((g) => (
          <Badge
            key={g.key}
            group={g.key}
            label={g.label}
            count={row.groups[g.key]}
            testId={`incon-badge-${row.code}-${g.key}`}
          />
        ))}
      </div>
      <ul className="mt-2 space-y-1.5">
        {row.problems.map((p) => (
          <li key={p.key} className="text-xs" data-testid={`incon-problem-${row.code}-${p.key}`}>
            <span className="font-semibold" style={{ color: GROUP_COLOR[p.group] }}>
              {p.label}
            </span>
            <span className="text-muted-foreground"> — {p.detail} </span>
            <button
              type="button"
              data-testid={`incon-fix-${row.code}-${p.key}`}
              disabled={busy}
              onClick={() => onFix(row, p.field)}
              className="underline decoration-dotted transition-opacity duration-200 hover:opacity-70 disabled:opacity-40"
            >
              fix this
            </button>
          </li>
        ))}
      </ul>
    </td>
    <td className="px-4 py-3 font-data text-xs tabular-nums text-muted-foreground">
      {row.term_shown}
      <span className="mt-1 block">{row.target_completion_on || "no committed date"}</span>
    </td>
    <td className="px-4 py-3 text-right">
      <div className="flex flex-col items-end gap-1.5">
        {canSmartFix && <SmartFixButton row={row} onDone={onSmartFixed} />}
        <button
          type="button"
          data-testid={`incon-open-${row.code}`}
          disabled={busy}
          onClick={() => onFix(row, row.problems[0]?.field || "")}
          className="inline-flex items-center gap-1.5 border border-border px-3 py-2 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted disabled:opacity-40"
        >
          {busy === row.id ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <Wrench className="h-3 w-3" />
          )}
          Fix
        </button>
      </div>
    </td>
  </tr>
);

export default function DataInconsistency() {
  const { user } = useAuth();
  // SDTD, admins and the unit's SPOC; a designated member's SmartFix waits for the SPOC.
  const canSmartFix = ["system_admin", "admin", "spoc", "spoc_member"].includes(user?.role);
  // The slicers live in the link, so an officer can bookmark or share their own unit's view — and
  // a unit line on the Dashboard sends an officer here already pointed at its own usecases.
  const [params, setParams] = useSearchParams();
  const plant = params.get("plant") || "";
  const type = params.get("type") || "";
  const status = params.get("status") || "";
  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (!key) ["plant", "type", "status"].forEach((k) => next.delete(k));
    else if (!value) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [group, setGroup] = useState("");
  const [q, setQ] = useState("");
  const [shown, setShown] = useState(PAGE);
  const [busy, setBusy] = useState("");
  const [fix, setFix] = useState(null);
  const [fixCompliance, setFixCompliance] = useState(null);

  const load = useCallback(() => {
    api
      .get("/inconsistency", { params: { plant, project_type: type, status, group, q } })
      .then((r) => {
        setData(r.data);
        setShown(PAGE);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [plant, type, status, group, q]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data));
  }, []);

  const openFix = async (row, field) => {
    // The settle-once compliance figures do not live on the register row — they are declared on
    // the usecase's compliance profile, so they are filled here in their own popup, writing into
    // the same single store the Compliance Data screen writes into.
    if (field === "compliance") {
      setFixCompliance(row.id);
      return;
    }
    setBusy(row.id);
    try {
      const { data: detail } = await api.get(`/projects/${row.id}`);
      setFix({ project: detail.project, field: field || "" });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  if (!data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );

  const rows = data.rows || [];
  const groups = data.groups || [];

  return (
    <div className="p-5 sm:p-8" data-testid="inconsistency-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="label-caps">What the register can prove is wrong</div>
          <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
            Data Inconsistency
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Every usecase on the register is read here — whatever its dates, and whether a unit has
            filled anything against it or not. Press <strong>Fix</strong> and the usecase form opens
            at the very entry that is wanted. A usecase drops off this list the moment it is clean,
            and a hole that has been filled never comes back.
            {data.own_unit_only ? ` You are seeing ${data.scope.join(", ")}.` : ""}
            {data.tv_only ? " Pravartanam-TV usecases only." : ""}
          </p>
        </div>
        <div className="max-w-sm">
          <ExportButton
            path="/inconsistency/template"
            params={{ plant, project_type: type, status }}
            label="Download the fix sheet"
            testId="incon-download"
          />
          <p className="mt-2 text-[11px] text-muted-foreground" data-testid="incon-offline-note">
            Work offline if that is easier. The fix sheet is the portal's own bulk-update workbook,
            cut down to the usecases above, with <b>what the portal says is wrong</b> printed
            beside each row. Correct it in Excel and bring it back on <b>Data files</b> — it is
            staged as a diff, refused whole if a row is wrong, and waits for the unit SPOC's
            approval exactly as the register workbook does.
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="incon-stats">
        {[
          ["incon-total", "Projects in this view", data.usecases, "Every usecase on the register",
           "inherit"],
          ["incon-complete", "Data complete", data.clean, "Nothing left to fix on these",
           "hsl(var(--healthy))"],
          ["incon-incomplete", "Data incomplete", data.unclean,
           "Carrying a hole, a contradiction or a date that disagrees", GROUP_COLOR.missing],
          ["incon-fields", "Entries to be fixed", data.open,
           `Across every unit in view, as on ${data.as_on}`, "hsl(0 72% 42%)"],
        ].map(([testId, label, value, note, colour]) => (
          <div key={testId} className="border border-border bg-card p-4">
            <div className="label-caps">{label}</div>
            <div
              className="font-data mt-1 text-3xl font-black tabular-nums"
              style={{ color: colour }}
              data-testid={testId}
            >
              {value ?? 0}
            </div>
            <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>
          </div>
        ))}
      </div>

      {!!data.light?.usecases && (
        <div
          className="mt-3 flex items-start gap-3 border border-[hsl(var(--healthy))] bg-[hsl(var(--healthy)/0.06)] p-4"
          data-testid="incon-light-note"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(var(--healthy))]" />
          <p className="text-xs leading-relaxed">
            <b data-testid="incon-light-count">{data.light.usecases}</b> completed usecase(s)
            outside Pravartanam-TV are asked for <b>their completion date only</b> —{" "}
            <b data-testid="incon-light-clean">{data.light.clean}</b> already read green. The portal
            prefilled the start and identification dates of{" "}
            <b data-testid="incon-light-prefilled">{data.light.prefilled}</b> of them (one year
            before completion); a unit may overwrite either. Sub-stage dates and benefit figures are
            not chased for these. Pravartanam-TV usecases are filled in full.
          </p>
        </div>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="incon-groups">
        {groups.map((g) => {
          const on = group === g.key;
          return (
            <button
              key={g.key}
              type="button"
              data-testid={`incon-group-${g.key}`}
              onClick={() => setGroup(on ? "" : g.key)}
              className={`border bg-card p-4 text-left transition-colors duration-200 ${
                on ? "border-foreground" : "border-border hover:bg-muted"
              }`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="label-caps" style={{ color: GROUP_COLOR[g.key] }}>
                  {g.label}
                </span>
                <span
                  className="font-data text-2xl font-black tabular-nums"
                  style={{ color: GROUP_COLOR[g.key] }}
                  data-testid={`incon-count-${g.key}`}
                >
                  {data.totals[g.key]}
                </span>
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">{g.note}</p>
              {on && (
                <p className="mt-2 font-display text-[10px] font-bold uppercase tracking-widest">
                  showing these only · press again to clear
                </p>
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Input
          className="h-11 min-w-[260px] flex-1 rounded-none"
          data-testid="incon-search"
          placeholder="Search by code, use case or officer…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <InconsistencySlicers
          data={data}
          meta={meta}
          plant={plant}
          type={type}
          status={status}
          setParam={setParam}
        />
      </div>

      <div className="mt-6 overflow-x-auto border border-border bg-card">
        <table className="w-full min-w-[980px] text-sm" data-testid="inconsistency-table">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="label-caps px-4 py-3 font-bold">Use case</th>
              <th className="label-caps px-4 py-3 font-bold">What is wrong</th>
              <th className="label-caps px-4 py-3 font-bold">Term · committed date</th>
              <th className="label-caps px-4 py-3 text-right font-bold">Put it right</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.slice(0, shown).map((row) => (
              <Row
                key={row.id}
                row={row}
                groups={groups}
                busy={busy === row.id ? row.id : ""}
                onFix={openFix}
                canSmartFix={canSmartFix}
                onSmartFixed={load}
              />
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={4} className="p-8 text-center text-muted-foreground">
                  <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-[hsl(var(--healthy))]" />
                  Nothing to put right here — every usecase in this view is clean.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {rows.length > shown && (
        <button
          type="button"
          data-testid="incon-more"
          onClick={() => setShown((n) => n + PAGE)}
          className="mt-3 border border-border bg-card px-4 py-2 font-display text-[10px] font-bold uppercase tracking-widest transition-colors duration-200 hover:bg-muted"
        >
          Show {Math.min(PAGE, rows.length - shown)} more of {rows.length}
        </button>
      )}

      <DuplicateGroups plant={plant} q={q} onChanged={load} />

      <ProjectFormDialog
        open={!!fix}
        onOpenChange={(o) => !o && setFix(null)}
        project={fix?.project || null}
        focusField={fix?.field || ""}
        meta={meta}
        onSaved={() => {
          setFix(null);
          load();
        }}
      />

      <ComplianceFixDialog
        projectId={fixCompliance}
        onClose={() => setFixCompliance(null)}
        onSaved={load}
      />
    </div>
  );
}
